import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import {
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container, Stack } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AtAGlance from '@/components/ui/AtAGlance';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1065Instructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 1065 Instructions (2025 TY) | Partnership Tax Guide"
          description="Detailed IRS Form 1065 instructions for partnerships. Learn how to complete each section, schedules, and K-1s. File accurately with TaxZerone"
          keywords=""
          canonical="resources/business-tax/form-1065-instructions/"
          ogType="article"
          jsonLdPricing="179.99"
          jsonLdAggregateRatingValue="4.97"
          jsonLdAggregateRatingCount="1658"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mt: 3,
          }}
        >
          IRS{' '}
          <span className="highlight-container">
            <span className="highlight"> Form 1065</span>
          </span>{' '}
          Instructions (2025)
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          U.S. Return of Partnership Income — Detailed Guidance
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1065instructions`}
          >
            E-File Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
        component="article"
        itemScope
        itemType="https://schema.org/Article"
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 2,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="flex-end"
                  sx={{ mb: 1, textAlign: 'right' }}
                >
                  <Typography variant="body2">
                    Last updated:{' '}
                    <time
                      itemProp="datePublished"
                      dateTime="2026-08-25T09:00:00-05:00"
                    >
                      Aug 25, 2026
                    </time>
                    <time
                      itemProp="dateModified"
                      dateTime="2026-08-25T09:00:00-05:00"
                      style={{ display: 'none' }}
                    >
                      Aug 25, 2026
                    </time>
                  </Typography>

                  <address
                    rel="author"
                    itemProp="author"
                    itemScope
                    itemType="https://schema.org/Person"
                    style={{ fontSize: 14, fontWeight: 600 }}
                  >
                    - by{' '}
                    <span itemProp="name">
                      <a
                        itemProp="url"
                        href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                        style={{
                          color: 'inherit',
                          textDecoration: 'none',
                        }}
                      >
                        Nikhil Chowdary
                      </a>
                    </span>{' '}
                    | TaxZerone
                  </address>
                </Stack>

                <AtAGlance
                  hrefUrl={`${process.env.NEXT_PUBLIC_HOMESITE_URL}resources/business-tax/form-1065-instructions/`}
                  headerText="Form 1065 at a Glance"
                >
                  <Typography
                    component="p"
                    itemProp="description"
                    variant="body1"
                  >
                    <span style={{ fontWeight: 600 }}> Form 1065 </span> is
                    filed by partnerships with{' '}
                    <span style={{ fontWeight: 600 }}> IRS</span> to report
                    income, deductions, credits, and other tax items. Income
                    generally passes through to partners and is reported on
                    <span style={{ fontWeight: 600 }}> Schedule K-1</span>.
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',
                      '& li': { mb: 1.6, position: 'relative', pl: 3 },
                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <Link
                          href="/resources/business-tax/form-1065-filing-deadlines-and-penalties-guide/"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          Due Date
                        </Link>
                        : <span style={{ fontWeight: 600 }}> March 15</span> for
                        calendar-year partnerships. Fiscal-year partnerships
                        generally file by the{' '}
                        <span style={{ fontWeight: 600 }}>
                          {' '}
                          15th day of the 3rd month
                        </span>{' '}
                        after the tax year ends
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <span style={{ fontWeight: 600 }}> Extension:</span>{' '}
                        <Link
                          href="/tax-extension/efile-irs-form-7004-business-tax-extension-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          File Form 7004
                        </Link>{' '}
                        by the original due date for an automatic 6-month
                        extension.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <span style={{ fontWeight: 600 }}>
                          {' '}
                          Late Filing Penalties:
                        </span>{' '}
                        The IRS may charge{' '}
                        <span style={{ fontWeight: 600 }}>
                          {' '}
                          $245 per partner per month
                        </span>{' '}
                        for late filing, plus penalties for missing or incorrect
                        Schedule K-1s.
                      </span>
                    </li>
                  </Box>
                </AtAGlance>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you operate a
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    partnership or a multi-member LLC, Form 1065
                  </span>{' '}
                  is the IRS return that reports your business’s annual
                  financial activity. While partnerships generally do not pay
                  federal income tax at the entity level, they are still
                  required to file this return to disclose income, deductions,
                  credits, and—most importantly—how those items are allocated
                  among partners.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Every amount reported on Form 1065 ultimately flows to
                  partners through{' '}
                  <span style={{ fontWeight: 600 }}>Schedule K-1</span>, which
                  they use to complete their individual or business tax returns.
                  This makes accuracy critical: an error on Form 1065 almost
                  always becomes an error on every partner’s return.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  This instruction guide is designed to walk you through Form
                  1065 <span style={{ fontWeight: 600 }}>step by step</span>,
                  explaining{' '}
                  <span style={{ fontWeight: 600 }}>
                    what each section means, why the IRS requires it, and how
                    the information flows
                  </span>{' '}
                  across schedules.
                </Typography>

                <Box
                  sx={{
                    pt: 0,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatis" sx={{ color: '#0202ff' }}>
                          What is Form 1065 and Why It Matters
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who Must File Form 1065
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#filing" sx={{ color: '#0202ff' }}>
                          Filing Deadlines, Extensions, and Penalties
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#key" sx={{ color: '#0202ff' }}>
                          Key Documents You Need Before Filing
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#sch" sx={{ color: '#0202ff' }}>
                          Key Schedules and Attachments
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#line" sx={{ color: '#0202ff' }}>
                          Line-by-Line Instructions
                        </Link>
                        <List sx={{ pb: 0 }}>
                          <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#basic" sx={{ color: '#0202ff' }}>
                                Basic Information Section
                              </Link>
                            </ListItemText>
                          </ListItem>
                          <ListItem
                            sx={{ py: 0.5, pb: 0, alignItems: 'flex-start' }}
                          >
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#income" sx={{ color: '#0202ff' }}>
                                Income Section
                              </Link>
                            </ListItemText>
                          </ListItem>
                          <ListItem
                            sx={{ py: 0.5, pb: 0, alignItems: 'flex-start' }}
                          >
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#section" sx={{ color: '#0202ff' }}>
                                Deductions Section
                              </Link>
                            </ListItemText>
                          </ListItem>
                          <ListItem
                            sx={{ py: 0.5, pb: 0, alignItems: 'flex-start' }}
                          >
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#tax" sx={{ color: '#0202ff' }}>
                                Tax and Payments Section
                              </Link>
                            </ListItemText>
                          </ListItem>
                        </List>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#schb" sx={{ color: '#0202ff' }}>
                          Schedule B – Other Information
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#schk" sx={{ color: '#0202ff' }}>
                          Schedule K – Partnership Summary
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#schk1" sx={{ color: '#0202ff' }}>
                          Schedule K-1 – Partner Allocations
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#schl" sx={{ color: '#0202ff' }}>
                          Schedule L – Balance Sheets
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#schm1" sx={{ color: '#0202ff' }}>
                          Schedule M-1 – Book vs Tax Reconciliation
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#schm2" sx={{ color: '#0202ff' }}>
                          Schedule M-2 – Partners’ Capital Accounts
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#how" sx={{ color: '#0202ff' }}>
                          How All Schedules Connect
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Common Mistakes to Avoid
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#lesser" sx={{ color: '#0202ff' }}>
                          Lesser-Known Forms Commonly Required With Form 1065
                        </Link>
                      </ListItemText>
                    </ListItem>
                    {/* <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#why" sx={{ color: '#0202ff' }}>
                          Why File Form 1065 Online with TaxZerone
                        </Link>
                      </ListItemText>
                    </ListItem> */}
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#checklist" sx={{ color: '#0202ff' }}>
                          Filing Checklist Before Submitting Form 1065
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#faq" id="whatis" sx={{ color: '#0202ff' }}>
                          Frequently Asked Questions (FAQs)
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What Is Form 1065 and Why It Matters
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 1065,
                  <span style={{ fontStyle: 'italic' }}>
                    {' '}
                    U.S. Return of Partnership Income
                  </span>
                  , is the annual federal tax return filed with the{' '}
                  <span style={{ fontWeight: 600 }}>Internal Revenue</span>{' '}
                  Service by partnerships and entities taxed as partnerships.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Form 1065 is used to report:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>Total partnership income and expenses </li>
                  <li>Ordinary business profit or loss </li>
                  <li>Separately stated income and deduction items </li>
                  <li>Credits and other tax attributes </li>
                  <li>Balance sheet information </li>
                  <li>Capital account activity for each partner </li>
                  <li>Allocation of all items to partners via Schedule K-1 </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontSize: { md: 20, sm: 18, xs: 18 },
                    fontWeight: 600,
                  }}
                >
                  Why Form 1065 Is So Important
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>Compliance </span>
                    <br />{' '}
                    <Link
                      href="/business-tax/efile-irs-form-1065-online/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Filing Form 1065
                    </Link>{' '}
                    is a legal requirement for most partnerships, and failure to
                    meet the{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Form 1065 deadline
                    </span>{' '}
                    can result in penalties calculated per partner, per month.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Partner reporting accuracy
                    </span>{' '}
                    <br />
                    Partners rely entirely on Schedule K-1 generated from 1065
                    form. Any mistake directly affects partners’ personal or
                    business tax returns.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Transparency to the IRS
                    </span>{' '}
                    <br />
                    The IRS uses 1065 tax form to evaluate ownership structure,
                    capital movements, foreign activity, and compliance with
                    partnership tax rules.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Audit risk reduction
                    </span>{' '}
                    <br />
                    Accurate completion of Schedules B, K, L, M-1, and M-2 helps
                    reduce the likelihood of IRS inquiries.
                  </li>
                </Box>
                <Alert
                  severity="info"
                  id="who"
                  icon={<InfoOutlinedIcon />}
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                >
                  <b>Important:</b> Even if your partnership had{' '}
                  <span style={{ fontWeight: 600 }}>
                    no income or minimal activity,
                  </span>{' '}
                  you may still be required to file Form 1065 to remain
                  compliant.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who Must File Form 1065?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  You must{' '}
                  <Link
                    href="/business-tax/efile-irs-form-1065-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file Form 1065
                  </Link>{' '}
                  if your business meets any of the following conditions during
                  the tax year:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    It is a{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      domestic partnership
                    </span>
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    It is a{' '}
                    <span style={{ fontWeight: 600 }}>multi-member LLC</span>{' '}
                    that has not elected corporate taxation
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    It had{' '}
                    <span style={{ fontWeight: 600 }}>
                      two or more partners at any time
                    </span>{' '}
                    during the year
                  </li>
                  <li>
                    It had{' '}
                    <span style={{ fontWeight: 600 }}>
                      income, expenses, gains, losses, or credits,
                    </span>{' '}
                    even if net income is zero
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    mt: 1,
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Entities Commonly Required to File
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>General partnerships </li>
                  <li>Limited partnerships (LPs) </li>
                  <li>Limited liability partnerships (LLPs) </li>
                  <li>Multi-member LLCs taxed as partnerships </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    mt: 1,
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Entities That Generally Do NOT File Form 1065
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>Sole proprietorships </li>
                  <li>
                    Single-member LLCs (unless electing partnership
                    treatment){' '}
                  </li>
                  <li>C Corporations (Form 1120) </li>
                  <li>
                    {' '}
                    <Link
                      href="/business-tax/efile-irs-form-1120-s/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      S Corporations (Form 1120-S)
                    </Link>{' '}
                  </li>
                </Box>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mt: 0, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                  id="filing"
                >
                  <b>Key distinction :</b> The obligation to file is based on
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    entity type and ownership,
                  </span>{' '}
                  not profitability.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Filing Deadlines, Extensions, and Penalties
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mt: 1,
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Standard Filing Deadline
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    <span style={{ fontWeight: 600 }}>
                      Calendar-year partnerships:
                    </span>{' '}
                    <span style={{ fontWeight: 600 }}> March 16, 2026</span>{' '}
                    (for the 2025 tax year)
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <span style={{ fontWeight: 600 }}>
                      Fiscal-year partnerships:
                    </span>{' '}
                    The{' '}
                    <span style={{ fontWeight: 600 }}>
                      15th day of the 3rd month
                    </span>{' '}
                    after the tax year ends
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the due date falls on a weekend or federal holiday, the
                  deadline shifts to the next business day.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mt: 1,
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Filing an Extension (Form 7004)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Partnerships can request an automatic{' '}
                  <span style={{ fontWeight: 600 }}>6-month extension</span> by
                  filing{' '}
                  <Link
                    href="/tax-extension/efile-irs-form-7004-business-tax-extension-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 7004
                  </Link>
                  .{' '}
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    No explanation is required
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    The extension moves the deadline to{' '}
                    <span style={{ fontWeight: 600 }}>September 15, 2026</span>
                  </li>
                  <li>
                    The extension applies to{' '}
                    <span style={{ fontWeight: 600 }}>filing only,</span> not to
                    paying any required taxes{' '}
                  </li>
                </ul>
                <Alert
                  severity="info"
                  id="who"
                  icon={<InfoOutlinedIcon />}
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                >
                  <b>Important:</b> Filing an extension prevents late-filing
                  penalties but does not excuse inaccurate or incomplete
                  returns.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    mt: 1,
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Penalties for Late or Incorrect Filing
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Situation
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Penalty
                          </TableCell>
                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            How It Applies
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>Late filing </TableCell>
                          <TableCell>$245 per partner, per month</TableCell>

                          <TableCell>Up to 12 months </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Missing Schedule K-1s </TableCell>
                          <TableCell>Additional penalties </TableCell>

                          <TableCell>Per partner </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>
                            Incomplete or inconsistent return{' '}
                          </TableCell>
                          <TableCell>IRS notices & audits </TableCell>

                          <TableCell>Common trigger </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            Failure to furnish K-1s to partners{' '}
                          </TableCell>
                          <TableCell>Separate penalties </TableCell>

                          <TableCell>Even if 1065 is filed </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Typography variant="body1" id="key" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}> Example:</span> A
                  partnership with 4 partners that files 3 months late could
                  face penalties exceeding{' '}
                  <span style={{ fontWeight: 600 }}>$2,800,</span> even if no
                  tax is owed.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Key Documents You Need Before Filing Form 1065
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Before starting taxes form 1065, gather and organize the
                  following records. Having these ready prevents errors and
                  saves time during filing.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{ fontWeight: 600, fontSize: { md: 18, xs: 18 } }}
                >
                  Financial Records
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>Profit & Loss statement for the tax year</li>
                  <li>Balance sheet (beginning and end of year)</li>
                  <li>Cost of goods sold details (if applicable)</li>
                  <li>Depreciation and amortization schedules</li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{ fontWeight: 600, fontSize: { md: 18, xs: 18 } }}
                >
                  Partner Information
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>Partner names and addresses</li>
                  <li>Ownership percentages </li>
                  <li>Profit, loss, and capital allocation agreements </li>
                  <li>Capital contributions and withdrawals </li>
                  <li>Guaranteed payment amounts </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{ fontWeight: 600, fontSize: { md: 18, xs: 18 } }}
                >
                  Prior-Year Information
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>Last year’s Form 1065 </li>
                  <li>Prior-year Schedule K-1s </li>
                  <li>Carryforward amounts (losses, credits, depreciation) </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Comparison: Form 1065 vs Form 1120 vs Form 1120-S
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  This comparison is provided for{' '}
                  <span style={{ fontWeight: 600 }}> context,</span> not
                  marketing, and helps filers confirm they are using the correct
                  form.
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              width: 150,
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Form
                          </TableCell>
                          <TableCell
                            sx={{
                              width: 300,
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Who Files
                          </TableCell>
                          <TableCell sx={{ width: 300, fontWeight: 600 }}>
                            Core Difference
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            1065
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Partnerships & multi-member LLCs
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Pass-through taxation; partner-level reporting
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            1120
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            C Corporations
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Corporation pays tax at entity level
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            1120-S
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            S Corporations
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Pass-through, but corporate ownership structure
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mt: 0, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                  id="line"
                >
                  <b> Key takeaway:</b> Partnerships and S Corporations are both
                  pass-through entities, but{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    capital accounts, allocations, and compliance rules differ
                    significantly
                  </span>
                  .
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                  id="basic"
                >
                  Form 1065 Line-by-Line Instructions
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Basic Information Section (Lines A–K) + Income Section (Lines
                  1a–8)
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  This section establishes the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    identity, structure, and filing context
                  </span>{' '}
                  of the partnership. Errors here commonly cause IRS notices or
                  return rejections.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1065-basic-info-img.webp" />
                  <source srcSet="/assets/images/f1065-basic-info-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1065-basic-info-img.png"
                    loading="lazy"
                    alt="Section (Lines A–K) + Income Section (Lines 1a–8) "
                    width={760}
                    height={199}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Name of Partnership
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    legal name of the partnership
                  </span>{' '}
                  exactly as shown on IRS EIN records.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Enter:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>Legal partnership or LLC name</li>
                </Box>

                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Do NOT enter:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>DBA or trade name unless it matches IRS records</li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Why this matters:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  {' '}
                  The IRS matches this field against the EIN database. Any
                  mismatch may delay processing.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Address (Street, City, State, ZIP / Country)
                </Typography>
                <Typography variant="body1">
                  Provide the partnership’s{' '}
                  <span style={{ fontWeight: 600 }}>
                    current mailing address
                  </span>
                  .
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    P.O. Box is allowed only if mail is not delivered to a
                    street address
                  </li>
                  <li>
                    Foreign partnerships must include country and postal code
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Common mistake:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Using an old address without checking the “Address change” box
                  (Line G).
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line A — Principal Business Activity
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Describe the{' '}
                  <span style={{ fontWeight: 600 }}>
                    main activity that generates income.
                  </span>
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Examples:
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>“Management consulting services”</li>
                  <li>“Residential real estate rental” </li>
                  <li>“Software development” </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Do NOT:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>Use vague terms like “business” or “services” </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Why IRS asks this:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  {' '}
                  To classify business operations and compare them against
                  industry norms.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line B — Principal Product or Service
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Explain{' '}
                  <span style={{ fontWeight: 600 }}>
                    what the partnership actually sells or provides.
                  </span>
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Examples:
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>“Web application development” </li>
                  <li>“Commercial property leasing” </li>
                  <li>“Tax preparation services” </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Difference from Line A:
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>Line A = type of activity </li>
                  <li>Line B = specific output </li>
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line C — Business Code Number (NAICS)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>6-digit NAICS code</span>{' '}
                  that best matches your activity.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Examples:
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>541511 – Custom Computer Programming Services </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Why this matters:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Incorrect codes may affect IRS risk scoring and industry
                  comparisons.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line D — Employer Identification Number (EIN)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the <span style={{ fontWeight: 600 }}>9-digit EIN</span>{' '}
                  assigned to the partnership.
                </Typography>
                <Alert
                  severity="info"
                  id="who"
                  icon={<InfoOutlinedIcon />}
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                >
                  <b>Important:</b>
                  <br />
                  <ul>
                    <li>Never enter a partner’s SSN </li>
                    <li>Ensure EIN matches IRS records exactly </li>
                  </ul>
                </Alert>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line E — Date Business Started
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the date the partnership{' '}
                  <span style={{ fontWeight: 600 }}>began operations,</span> not
                  the EIN issuance date.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Examples:
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>Partnership agreement signed: May 10, 2023 </li>
                  <li>
                    Business activity began: June 1, 2023 Enter June 1,
                    2023{' '}
                  </li>
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line F — Total Assets (End of Year)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    total book value of assets
                  </span>{' '}
                  at the end of the tax year.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Includes:
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Cash </li>
                  <li>Receivables</li>
                  <li>Equipment </li>
                  <li>Property </li>
                  <li>Investments </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Do NOT:
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Use market value </li>
                  <li>Leave blank if assets exist </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Flow impact:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  This number must match{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Schedule L, End of Year – Total Assets.
                  </span>
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line G — Type of Return (Check All That Apply)
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Check applicable boxes:
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}> Initial return –</span>{' '}
                    First year filing{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Final return –</span>{' '}
                    Partnership closed{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Name change –</span> Legal
                    name changed{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Address change –</span>{' '}
                    Mailing address changed{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Amended return –</span>{' '}
                    Correcting a previously filed return{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Example:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the partnership closed in 2025 Check{' '}
                  <span style={{ fontWeight: 600 }}>Final return </span>
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line H — Accounting Method
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Choose <span style={{ fontWeight: 600 }}>one: </span>
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}> Cash –</span> Income
                    reported when received
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Accrual –</span> Income
                    reported when earned{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Other </span> Specify
                    (e.g., hybrid method){' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Important:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Method must match prior-year filings unless IRS approval was
                  obtained.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line I — Number of Schedules K-1
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the total number of{' '}
                  <span style={{ fontWeight: 600 }}>
                    partners at any time during the year.
                  </span>
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Include:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Partners who joined or left mid-year </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Why IRS asks this:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  To confirm all partners receive a Schedule K-1.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line J — Schedules C and M-3 Attached
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Check if either is attached.
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Schedule M-3 is typically required for large
                    partnerships{' '}
                  </li>
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line K — Activity Grouping Elections
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Check applicable boxes if:
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Activities are aggregated for{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      at-risk rules (Section 465){' '}
                    </span>
                  </li>
                  <li>
                    Activities are grouped for{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      passive activity rules (Section 469)
                    </span>
                  </li>
                </Box>
                <Typography
                  variant="body1"
                  id="income"
                  sx={{ fontWeight: 600 }}
                >
                  Why IRS asks this:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Affects how losses are limited and reported to partners.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Income Section (Lines 1a–8)
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1065-income-sec-img.webp" />
                  <source srcSet="/assets/images/f1065-income-sec-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1065-income-sec-img.png"
                    loading="lazy"
                    alt="Income Section (Lines 1a–8)"
                    width={760}
                    height={132}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  This section reports the partnership’s{' '}
                  <span style={{ fontWeight: 600 }}>
                    gross income and operating results
                  </span>{' '}
                  before expenses.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  š ï¸ Only{' '}
                  <span style={{ fontWeight: 600 }}>
                    trade or business income
                  </span>{' '}
                  belongs here. Investment income is usually reported separately
                  on Schedule K.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 1a — Gross Receipts or Sales
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter{' '}
                  <span style={{ fontWeight: 600 }}>
                    total sales or service income
                  </span>{' '}
                  before any deductions.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Example:
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Client billings during year:{' '}
                    <span style={{ fontWeight: 600 }}>$750,000</span>
                  </li>
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 1b — Returns and Allowances
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter refunds, discounts, or returned goods.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Example:
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Client refunds issued:{' '}
                    <span style={{ fontWeight: 600 }}>$20,000 </span>
                  </li>
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 1c — Balance (Net Sales)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Automatically calculated
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Line 1c = Line 1a – Line 1b
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Example:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  $750,000 – $20,000 ={' '}
                  <span style={{ fontWeight: 600 }}> $730,000 </span>
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 2 — Cost of Goods Sold (COGS)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the cost of producing or acquiring goods sold.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Attach:
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    <Link
                      href="https://www.irs.gov/forms-pubs/about-form-1125-a"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 1125-A{' '}
                    </Link>
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Includes:
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Inventory costs</li>
                  <li>Direct labor </li>
                  <li>Materials </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Do NOT include:
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Operating expenses (rent, admin salaries)</li>
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 3 — Gross Profit
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Calculated as:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                  Line 3 = Line 1c – Line 2
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Example:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  $730,000 – $280,000 ={' '}
                  <span style={{ fontWeight: 600 }}> $450,000 </span>
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 4 — Ordinary Income (Loss) from Other Partnerships,
                  Estates, and Trusts
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report pass-through income from:
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Other partnerships</li>
                  <li>Estates or trusts </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Attach statement</span>{' '}
                  listing each source.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 5 — Net Farm Profit (Loss)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter farm income or loss.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Attach:
                </Typography>

                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>
                    <Link
                      href="https://www.irs.gov/forms-pubs/about-schedule-f-form-1040"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Schedule F (Form 1040)
                    </Link>{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Most non-farm partnerships leave this blank.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 6 — Net Gain (Loss) from Form 4797
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Report gains or losses from sales of:
                </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>Equipment</li>
                  <li>Vehicles </li>
                  <li>Business property </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Attach:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    {' '}
                    <Link
                      href="https://www.irs.gov/forms-pubs/about-form-4797"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 4797
                    </Link>{' '}
                  </li>
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 7 — Other Income (Loss)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Report income not listed elsewhere, such as:
                </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>Cancellation of debt income </li>
                  <li>Refunds </li>
                  <li>Miscellaneous business income </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}> Attach statement</span>{' '}
                  explaining each item.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 8 — Total Income (Loss)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Add Lines <span style={{ fontWeight: 600 }}>3 through 7</span>
                  .
                </Typography>

                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Report income not listed elsewhere, such as:
                </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>
                    Gross profit (Line 3):{' '}
                    <span style={{ fontWeight: 600 }}> $450,000</span>{' '}
                  </li>
                  <li>
                    Other partnership income (Line 4):{' '}
                    <span style={{ fontWeight: 600 }}> $15,000</span>{' '}
                  </li>
                  <li>
                    Asset gain (Line 6):{' '}
                    <span style={{ fontWeight: 600 }}> $10,000</span>{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>
                    Total Income (Line 8):
                  </span>{' '}
                  $475,000
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  How These Numbers Flow Next
                </Typography>
                <Box component="ul" sx={{ mb: 4, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}> Line 8</span> feeds into
                    the{' '}
                    <span style={{ fontWeight: 600 }}>Deductions Section </span>
                  </li>
                  <li>
                    Final result becomes{' '}
                    <span style={{ fontWeight: 600 }}>
                      Ordinary Business Income (Line 23)
                    </span>
                  </li>
                  <li>Line 23 flows to: </li>
                  <ul>
                    <li>
                      <span style={{ fontWeight: 600 }} id="section">
                        {' '}
                        Schedule K, Line 1
                      </span>{' '}
                    </li>
                    <li>
                      <span style={{ fontWeight: 600 }}>
                        Each partner’s Schedule K-1
                      </span>{' '}
                    </li>
                  </ul>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Deductions Section (Lines 9–23) & Tax and Payments (Lines
                  24–32)
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1065-deduction-sec-img.webp" />
                  <source srcSet="/assets/images/f1065-deduction-sec-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1065-deduction-sec-img.png"
                    loading="lazy"
                    alt="Deductions Section (Lines 9–23)"
                    width={760}
                    height={245}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 20, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1.5,
                  }}
                >
                  Deductions Section (Lines 9–23)
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  This section reports{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    ordinary and necessary business expenses
                  </span>{' '}
                  incurred by the partnership. These deductions reduce the
                  partnership’s taxable income, which directly impacts each
                  partner’s Schedule K-1.
                </Typography>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mt: 0, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                  id="line"
                >
                  <b> Key Rule:</b> Partners are{' '}
                  <span style={{ fontWeight: 600 }}> not employees</span>.
                  Payments to partners are treated differently from employee
                  wages. .
                </Alert>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 9 — Salaries and Wages (Other Than to Partners)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Report wages paid to{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    non-partner employees only
                  </span>
                  .
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Include:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Employee salaries and hourly wages </li>
                  <li>Bonuses paid to employees </li>
                  <li>Taxable fringe benefits </li>
                  <li>Employer-paid payroll taxes (after credits) </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Do NOT include:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Payments to partners </li>
                  <li>Guaranteed payments (those go on Line 10) </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Example:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Office staff wages: $180,000 </li>
                  <li>Payroll tax credits used: $10,000 </li>
                  <li>
                    Amount entered:{' '}
                    <span style={{ fontWeight: 600 }}> $170,000</span>{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Why IRS separates this:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Partners cannot be treated as{' '}
                  <Link
                    href="https://www.irs.gov/forms-pubs/about-form-w-2"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    W-2 employees
                  </Link>{' '}
                  for federal tax purposes.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 10 — Guaranteed Payments to Partners
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Guaranteed payments are amounts paid to partners without
                  regard to partnership income.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Common guaranteed payments include:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Compensation for services </li>
                  <li>Payments for use of capital </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Example:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Partner A receives $60,000 annually for managing operations
                    Guaranteed payment{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Important tax treatment:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Deductible by the partnership</li>
                  <li>Taxable to the partner </li>
                  <li>Subject to self-employment tax </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  K-1 impact:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Reported separately on Schedule K and each partner’s Schedule
                  K-1.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 11 — Repairs and Maintenance
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Costs to{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    keep property in normal operating condition
                  </span>
                  .
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Include:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Equipment repairs </li>
                  <li>Routine maintenance </li>
                  <li>Painting, servicing, fixing </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Do NOT include:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Improvements that increase value or extend life (capitalize
                    instead){' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Example:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Equipment servicing: $7,500</li>
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 12 — Bad Debts
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Deduct debts that:
                </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>Were previously included in income, and </li>
                  <li>Are now uncollectible </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Important:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Most partnerships use the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    specific charge-off method,
                  </span>{' '}
                  not estimates.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Example:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Client invoice of $4,000 written off as uncollectible{' '}
                  </li>
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 13 — Rent
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Report rent paid for:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Office space</li>
                  <li>Equipment </li>
                  <li>Vehicles </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Do NOT include:
                </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>
                    Rent paid to a partner (may require special disclosure){' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Example:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Office lease: $30,000 annually</li>
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 14 — Taxes and Licenses
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Include:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>State and local business taxes </li>
                  <li>Payroll taxes </li>
                  <li>Business licenses and permits </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Exclude:
                </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>Federal income taxes</li>
                  <li>Penalties and fines </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Example:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>State franchise tax: $9,000 </li>
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 15 — Interest
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Interest paid on:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Business loans </li>
                  <li>Mortgages </li>
                  <li>Lines of credit </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Do NOT include:
                </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>Personal interest of partners </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Special rule:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Business interest may be limited under{' '}
                  <span style={{ fontWeight: 600 }}> Section 163(j)</span> in
                  some cases.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 16 — Depreciation
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 16a — Depreciation
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter total depreciation expense.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 16b — Depreciation reported elsewhere
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract depreciation already included in COGS or other lines.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 16c — Net Depreciation
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Automatically calculated.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Attach:
                </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>
                    {' '}
                    <Link
                      href="https://www.irs.gov/forms-pubs/about-form-4562"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 4562
                    </Link>{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Example:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Equipment depreciation: $18,000 </li>
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 17 — Depletion
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Used by businesses extracting natural resources.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Do NOT deduct oil & gas depletion here
                  </span>{' '}
                  (reported elsewhere).
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 18 — Retirement Plans, etc.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Include employer contributions to:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>SEP IRAs </li>
                  <li>SIMPLE plans </li>
                  <li>Qualified retirement plans </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Example:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>SEP contributions: $14,000 </li>
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 19 — Employee Benefit Programs
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Include:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Health insurance </li>
                  <li>Life insurance </li>
                  <li>Disability insurance </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Exclude:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Retirement benefits (Line 18) </li>
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 20 — Energy Efficient Commercial Buildings Deduction
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Deduct qualifying energy-efficient building improvements.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Attach:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    <Link
                      href="https://www.irs.gov/forms-pubs/about-form-7205"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 7205
                    </Link>{' '}
                  </li>
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 21 — Other Deductions
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Report expenses not listed elsewhere.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Examples:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Legal and accounting fees</li>
                  <li>Advertising </li>
                  <li>Office supplies </li>
                  <li>Utilities </li>
                  <li>Software subscriptions </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}> Attach a statement</span>{' '}
                  itemizing each category.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 22 — Total Deductions
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add Lines{' '}
                  <span style={{ fontWeight: 600 }}>9 through 21</span>.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 23 — Ordinary Business Income (Loss)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Subtract total deductions from total income:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 23 = Line 8 – Line 22
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Example:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Total income (Line 8): $475,000</li>
                  <li>Total deductions (Line 22): $430,000 </li>
                  <li>
                    Ordinary income:{' '}
                    <span style={{ fontWeight: 600 }}>$45,000</span>{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Flow impact:
                </Typography>
                <Box component="ul" sx={{ mb: 4, '& li': { mb: 1.2 } }}>
                  <li>
                    Reported on{' '}
                    <span id="tax" style={{ fontWeight: 600 }}>
                      Schedule K, Line 1
                    </span>{' '}
                  </li>
                  <li>
                    Allocated to partners on{' '}
                    <span style={{ fontWeight: 600 }}>Schedule K-1</span>{' '}
                  </li>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Tax and Payments Section (Lines 24–32)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Most partnerships{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    do not pay federal income tax,
                  </span>{' '}
                  but these lines apply in special situations.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1065-tax-pay-sec-img.webp" />
                  <source srcSet="/assets/images/f1065-tax-pay-sec-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1065-tax-pay-sec-img.png"
                    loading="lazy"
                    alt="Tax and Payments Section (Lines 24–32)"
                    width={760}
                    height={147}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 24 — Interest Due (Look-Back Method – Long-Term
                  Contracts)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Used only if the partnership applies the look-back method.
                </Typography>

                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Attach:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    <Link
                      href="https://www.irs.gov/forms-pubs/about-form-8697"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 8697
                    </Link>{' '}
                  </li>
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 25 — Interest Due (Income Forecast Method)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Applies to income forecast property.
                </Typography>

                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Attach:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    <Link
                      href="https://www.irs.gov/forms-pubs/about-form-8866"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 8866
                    </Link>{' '}
                  </li>
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 26 — BBA AAR Imputed Underpayment
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Used when partnership audit adjustments result in entity-level
                  tax.
                </Typography>

                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Important:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  This applies under the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    centralized partnership audit regime
                  </span>
                  .
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 27 — Other Taxes
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Includes:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Excise taxes</li>
                  <li>Recapture taxes </li>
                  <li>Other partnership-level taxes </li>
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 28 — Total Balance Due
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add Lines{' '}
                  <span style={{ fontWeight: 600 }}> 24 through 27</span>.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 29 — Elective Payment Election Amount
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Credits elected for direct payment (from{' '}
                  <Link
                    href="https://www.irs.gov/forms-pubs/about-form-3800"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 3800
                  </Link>
                  , if applicable).
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 30 — Payment
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Enter any tax payments made with:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Extensions </li>
                  <li>Estimated payments </li>
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 31 — Amount Owed
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If payments are{' '}
                  <span style={{ fontWeight: 600 }}>less than</span> balance
                  due, enter the difference.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 32 — Overpayment
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If payments <span style={{ fontWeight: 600 }}>exceed</span>{' '}
                  balance due, enter the excess.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 19, md: 19, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  How Phase 3 Connects to the Rest of the Return
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Line 23 feeds{' '}
                    <span style={{ fontWeight: 600 }}> Schedule K</span>{' '}
                  </li>
                  <li>Guaranteed payments flow separately to partners </li>
                  <li>
                    Deductions affect{' '}
                    <span id="schb" style={{ fontWeight: 600 }}>
                      partner basis
                    </span>{' '}
                  </li>
                  <li>
                    Incorrect classification here often causes{' '}
                    <span style={{ fontWeight: 600 }}>K-1 errors</span>{' '}
                  </li>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 2,
                  }}
                >
                  Schedule B (Form 1065) — Other Information
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Question-by-Question Instructions & IRS Logic
                </Typography>
                <Typography variant="body1" id="pro" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> Schedule B</span> is not a
                  financial schedule — it is a{' '}
                  <span style={{ fontWeight: 600 }}>
                    compliance, disclosure, and risk-assessment schedule
                  </span>
                  .
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The{' '}
                  <span style={{ fontWeight: 600 }}>
                    Internal Revenue Service
                  </span>{' '}
                  uses Schedule B to determine:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Ownership concentration </li>
                  <li>Foreign exposure </li>
                  <li>Audit regime applicability </li>
                  <li>Disclosure compliance </li>
                  <li>Whether additional forms are required </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={<ReportProblemIcon />}
                  sx={{ mb: 3, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Critical: </b> Incorrect answers on Schedule B are one of
                  the <span style={{ fontWeight: 600 }}> top triggers</span> for
                  IRS correspondence and partnership audits.
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 20, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Question 1 — Type of Entity Filing the Return
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  You must identify the{' '}
                  <span style={{ fontWeight: 600 }}>legal structure</span> of
                  the partnership.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Options include:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Domestic general partnership </li>
                  <li>Domestic limited partnership </li>
                  <li>Domestic limited liability company (LLC) </li>
                  <li>Domestic limited liability partnership (LLP) </li>
                  <li>Foreign partnership </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Why IRS asks this:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Different partnership structures are subject to{' '}
                  <span style={{ fontWeight: 600 }}>
                    different liability, reporting, and audit rules
                  </span>
                  .
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Common mistake:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Checking “ form 1065 for LLC” without confirming the entity is{' '}
                  <span style={{ fontWeight: 600 }}>
                    taxed as a partnership
                  </span>
                  .
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 20, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Question 2 — Ownership of 50% or More
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  2(a) — Entity Ownership (Corporations, Trusts, Governments)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Answer <span style={{ fontWeight: 600 }}>Yes</span> if{' '}
                  <span style={{ fontWeight: 600 }}>any entity</span> directly
                  or indirectly owned{' '}
                  <span style={{ fontWeight: 600 }}>50% or more</span> of
                  profits, losses, or capital.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Includes constructive ownership </li>
                  <li>Includes indirect chains </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  If “Yes”:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    You must attach{' '}
                    <span style={{ fontWeight: 600 }}> Schedule B-1</span>{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  2(b) — Individual or Estate Ownership
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Answer <span style={{ fontWeight: 600 }}>Yes</span> if any{' '}
                  <span style={{ fontWeight: 600 }}>individual or estate</span>{' '}
                  owned <span style={{ fontWeight: 600 }}>50% or more</span>
                  .{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Why this matters:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  High ownership concentration increases IRS scrutiny of:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Allocation accuracy </li>
                  <li>Disguised compensation</li>
                  <li>Basis manipulation </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 20, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Question 3 — Ownership of Other Entities
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  3(a) — Ownership of Corporations
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Answer <span style={{ fontWeight: 600 }}> Yes</span> if the
                  partnership owned:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      20% or more directly,
                    </span>{' '}
                    OR{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      50% or more directly or indirectly
                    </span>{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You must report:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Corporation name </li>
                  <li>EIN </li>
                  <li>Country </li>
                  <li>Ownership percentage </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  3(b) — Ownership of Other Partnerships or Trusts
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Answer <span style={{ fontWeight: 600 }}> Yes</span> if the
                  partnership owned:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>20% or more of profits/losses/capital, OR</li>
                  <li>50% or more directly or indirectly</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Why IRS tracks this:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  To identify{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    tiered partnership structures,
                  </span>{' '}
                  which are high-risk for income shifting.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 20, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Question 4 — Small Partnership Exception
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Answer <span style={{ fontWeight: 600 }}> Yes</span> only if{' '}
                  <span style={{ fontWeight: 600 }}>all four conditions</span>{' '}
                  are met:
                </Typography>
                <Box component="ol" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Gross receipts &lt; $250,000</li>
                  <li>Total assets &lt; $1 million</li>
                  <li>All form 1065 k1 filed on time </li>
                  <li>Not required to file Schedule M-3 </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  If “Yes”:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>You may skip: </li>
                  <ul>
                    <li>Schedule L </li>
                    <li>Schedule M-1 </li>
                    <li>Schedule M-2 </li>
                    <li>Item F (Total Assets) </li>
                  </ul>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Common error:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Claiming this exception while still having foreign activity or
                  complex allocations.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 20, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Question 5 — Publicly Traded Partnership (PTP)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Answer <span style={{ fontWeight: 600 }}> Yes</span> if
                  interests are traded on:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Public markets, OR </li>
                  <li>Secondary markets </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  PTPs are subject to{' '}
                  <span style={{ fontWeight: 600 }}>
                    special passive activity rules
                  </span>
                  .
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 20, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Question 6 — Canceled or Modified Debt
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Answer <span style={{ fontWeight: 600 }}> Yes</span> if any
                  partnership debt:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Was forgiven </li>
                  <li>Was canceled </li>
                  <li>Had principal reduced </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  If “Yes”:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Cancellation of Debt (COD) income may apply </li>
                  <li>Additional reporting may be required </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 20, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Question 7 — Reportable Transactions (Form 8918)
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Answer <span style={{ fontWeight: 600 }}> Yes</span> if the
                  partnership participated in a{' '}
                  <span style={{ fontWeight: 600 }}>
                    reportable transaction
                  </span>
                  .
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  These are transactions the IRS considers{' '}
                  <span style={{ fontWeight: 600 }}>
                    potential tax avoidance
                  </span>
                  .
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  If “Yes”:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Form 8918 must be filed </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 20, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Question 8 — Foreign Financial Accounts (FBAR)
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Answer <span style={{ fontWeight: 600 }}> Yes</span> if the
                  partnership had:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Foreign bank accounts </li>
                  <li>Foreign securities accounts </li>
                  <li>Signing authority over foreign accounts </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  If “Yes”:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>FBAR (FinCEN Form 114) may be required </li>
                  <li>Country name must be listed </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Common misconception:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  This is <span style={{ fontWeight: 600 }}>not</span> reported
                  on the tax return itself — but failure to disclose here is
                  penalized.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 20, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Question 9 — Foreign Trust Transactions
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Answer <span style={{ fontWeight: 600 }}> Yes</span> if the
                  partnership:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Received distributions from a foreign trust </li>
                  <li>Transferred assets to a foreign trust </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  If “Yes”:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Form 3520 may be required </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 20, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Question 10 — Section 754 & Basis Adjustments
                </Typography>

                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  10(a) — Section 754 Election
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Answer <span style={{ fontWeight: 600 }}> Yes</span> if the
                  partnership has an active{' '}
                  <span style={{ fontWeight: 600 }}>Section 754 election</span>.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Why this matters:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This election allows basis adjustments when:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Partners enter or exit </li>
                  <li>Interests are transferred </li>
                  <li>Assets are distributed </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Once made, the election generally{' '}
                  <span style={{ fontWeight: 600 }}>
                    cannot be revoked without IRS consent
                  </span>
                  .
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  10(b) — Section 743(b) Adjustments
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Answer <span style={{ fontWeight: 600 }}> Yes</span> if basis
                  was adjusted for{' '}
                  <span style={{ fontWeight: 600 }}>
                    partner-level transfers
                  </span>
                  .
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  You must report:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Net positive adjustments </li>
                  <li>Net negative adjustments </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  10(c) — Section 734(b) Adjustments
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Applies to{' '}
                  <span style={{ fontWeight: 600 }}>
                    property distributions
                  </span>
                  .
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Used when distributions trigger basis adjustments at the{' '}
                  <span style={{ fontWeight: 600 }}>partnership level</span>.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  10(d) — Mandatory Basis Adjustments
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Answer <span style={{ fontWeight: 600 }}>Yes</span> if
                  adjustments were required due to:
                </Typography>

                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>Substantial built-in loss </li>
                  <li>Substantial basis reduction </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Failure to comply here is a{' '}
                  <span style={{ fontWeight: 600 }}>high-risk audit issue</span>
                  .
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Questions 11–12 — Property Distributions
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Question 11 — Like-Kind Exchange Property
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Answer <span style={{ fontWeight: 600 }}>Yes</span> if
                  property from a like-kind exchange was:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Distributed, OR </li>
                  <li>Contributed to another entity </li>
                </Box>

                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Question 12 — Tenancy-in-Common Interests
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Answer <span style={{ fontWeight: 600 }}>Yes</span> if
                  undivided property interests were distributed.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  These transactions require careful capital and basis tracking.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 20, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Questions 13–18 — Foreign Reporting Forms
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Includes:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    {' '}
                    <Link
                      href="https://www.irs.gov/forms-pubs/about-form-8858"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 8858
                    </Link>{' '}
                    (Foreign branches){' '}
                  </li>
                  <li>
                    {' '}
                    <Link
                      href="https://www.irs.gov/forms-pubs/about-form-8805"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 8805
                    </Link>{' '}
                    (Foreign partner withholding){' '}
                  </li>
                  <li>
                    {' '}
                    <Link
                      href="https://www.irs.gov/forms-pubs/about-form-8865"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 8865
                    </Link>{' '}
                    (Foreign partnerships){' '}
                  </li>
                  <li>
                    <Link
                      href="https://www.irs.gov/forms-pubs/about-form-5471"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 5471
                    </Link>{' '}
                    (Foreign corporations)
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  If “Yes”:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    You must enter the{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      number of forms attached
                    </span>{' '}
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 20, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Question 19 — Forms 1042 / 1042-S (Withholding)
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Answer <span style={{ fontWeight: 600 }}>Yes</span> if the
                  partnership made payments to foreign persons subject to
                  withholding.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 20, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Question 20 — Form 8938 (FATCA)
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Answer <span style={{ fontWeight: 600 }}>Yes</span> if the
                  partnership is required to disclose specified foreign
                  financial assets.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 20, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Questions 21–24 — Interest Limitation & Tax Shelter Rules
                </Typography>
                <Typography variant="body1">Covers: </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>Section 721(c) partnerships </li>
                  <li>Disallowed interest deductions </li>
                  <li>Section 163(j) elections </li>
                  <li>Form 8990 requirements </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  These questions identify{' '}
                  <span style={{ fontWeight: 600 }}>
                    earnings-stripping and interest abuse.
                  </span>
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 20, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Questions 25–26 — Opportunity Zones & Foreign Transfers
                </Typography>
                <Typography variant="body1">Includes: </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Qualified Opportunity Fund certification (Form 8996) </li>
                  <li>Transfers triggering Section 864(c)(8) </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 20, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Questions 27–29 — Advanced Compliance Areas
                </Typography>
                <Typography variant="body1">Covers: </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Related-party transfers </li>
                  <li>Inversion transactions </li>
                  <li>Excise tax on stock repurchases (Form 7208) </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 20, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Question 30 — Digital Asset Transactions
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Answer <span style={{ fontWeight: 600 }}>Yes</span> if the
                  partnership:
                </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>Received digital assets as payment, OR </li>
                  <li>Sold or exchanged digital assets </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  This includes cryptocurrencies and certain tokens.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 20, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Question 32 — Election Out of Subchapter K
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Rare election allowing partnerships to opt out of standard
                  partnership tax rules.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 20, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Question 33 — Centralized Partnership Audit Regime
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  If “Yes”:
                </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>
                    You must complete{' '}
                    <span style={{ fontWeight: 600 }}> Schedule B-2</span>{' '}
                  </li>
                  <li>Partnership elects out of centralized audits </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  If “No”:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    You must designate a{' '}
                    <span style={{ fontWeight: 600 }}>
                      Partnership Representative (PR)
                    </span>{' '}
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 20, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Designation of Partnership Representative (PR)
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  The PR:
                </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>Has sole authority to deal with IRS audits</li>
                  <li>
                    Does <span style={{ fontWeight: 600 }}>not</span> need to be
                    a partner{' '}
                  </li>
                  <li>Can bind the partnership legally </li>
                </Box>

                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Important:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  {' '}
                  This role replaces the old “tax matters partner.”
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 20, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Why Schedule B Errors Are Dangerous
                </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>Triggers IRS notices </li>
                  <li>Forces amended returns </li>
                  <li>Causes K-1 corrections </li>
                  <li>Increases audit probability </li>
                </Box>
                <Alert
                  severity="info"
                  icon={<InfoOutlinedIcon />}
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                >
                  <b> Pro Tip:</b> Always answer Schedule B{' '}
                  <span style={{ fontWeight: 600 }}> before</span> completing
                  Schedule K and K-1s.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 2,
                  }}
                >
                  Schedules K, K-1, L, M-1, and M-2
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 20, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Detailed Line-by-Line Partnership Reporting Guide
                </Typography>
                <Typography variant="body1" id="schk" sx={{ mb: 3 }}>
                  These schedules explain{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    how partnership results are summarized, allocated,
                    reconciled, and tracked.
                  </span>{' '}
                  Errors here almost always lead to{' '}
                  <span style={{ fontWeight: 600 }}>
                    partner notices, amended K-1s, or audits
                  </span>{' '}
                  by the{' '}
                  <span style={{ fontWeight: 600 }}>
                    Internal Revenue Service
                  </span>
                  .
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 2,
                  }}
                >
                  Schedule K — Partners’ Distributive Share Items (Summary
                  Level)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Schedule K</span> reports
                  the{' '}
                  <span style={{ fontWeight: 600 }}>
                    total partnership amounts
                  </span>{' '}
                  for each category of income, deduction, credit, and other tax
                  item.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Nothing here is partner-specific — allocations happen later on
                  <span style={{ fontWeight: 600 }}> Schedule K-1</span>.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1065-schk-partner-img.webp" />
                  <source srcSet="/assets/images/f1065-schk-partner-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1065-schk-partner-img.png"
                    loading="lazy"
                    alt="Schedule K — Partners’ Distributive Share Items"
                    width={759}
                    height={281}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 20, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Schedule K — Income (Loss)
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 17, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 1 — Ordinary Business Income (Loss)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  This comes directly from{' '}
                  <span style={{ fontWeight: 600 }}>Page 1, Line 23</span>.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.2, fontWeight: 600 }}>
                  What it represents:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Net operating profit or loss from the partnership’s core
                  business.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Example:
                </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>
                    Ordinary business income:{' '}
                    <span style={{ fontWeight: 600 }}>$120,000</span>{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  This amount is later split among partners based on the
                  partnership agreement.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 17, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 2 — Net Rental Real Estate Income (Loss)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Income or loss from rental real estate activities.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.2, fontWeight: 600 }}>
                  Attach:
                </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>
                    {' '}
                    <Link
                      href="https://www.irs.gov/forms-pubs/about-form-8825"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 8825
                    </Link>
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Why IRS separates this:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Rental real estate is subject to{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    passive activity rules
                  </span>
                  .
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 17, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 3 — Other Rental Income (Loss)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  For rental activities{' '}
                  <span style={{ fontWeight: 600 }}>
                    not involving real estate,
                  </span>{' '}
                  such as equipment leasing.
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>3a: Gross rental income </li>
                  <li>3b: Expenses </li>
                  <li>3c: Net rental income (loss) </li>
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 17, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 4 — Guaranteed Payments
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Total guaranteed payments made to partners.
                </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>4a: For services </li>
                  <li>4b: For capital </li>
                  <li>4c: Total </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Important:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Guaranteed payments are{' '}
                  <span style={{ fontWeight: 600 }}> not allocated</span> like
                  profits — they are reported separately.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 17, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 5 — Interest Income
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Interest earned on:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Bank accounts </li>
                  <li>Notes receivable </li>
                  <li>Investments </li>
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 17, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 6 — Dividends
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Interest earned on:
                </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>6a: Ordinary dividends </li>
                  <li>6b: Qualified dividends </li>
                  <li>6c: Dividend equivalents </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Qualified dividends may receive{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    preferential tax rates{' '}
                  </span>{' '}
                  at the partner level.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 17, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 7 — Royalties
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Income from intellectual property, mineral rights, or
                  licensing agreements.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 17, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Lines 8–10 — Capital Gains and Section 1231 Items
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Includes:
                </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>Short-term capital gains </li>
                  <li>Long-term capital gains </li>
                  <li>Collectibles gains </li>
                  <li>Unrecaptured Section 1250 gains </li>
                  <li>Section 1231 gains/losses</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Attach:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    {' '}
                    <Link
                      href="https://www.irs.gov/pub/irs-pdf/f1065sd.pdf"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Schedule D (Form 1065){' '}
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="https://www.irs.gov/forms-pubs/about-form-4797"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 4797
                    </Link>{' '}
                    (when applicable){' '}
                  </li>
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 17, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 11 — Other Income (Loss)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Income not classified elsewhere.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Examples:
                </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>Cancellation of debt income </li>
                  <li>Litigation settlements </li>
                  <li>Insurance proceeds </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Attach statements specifying each item.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Schedule K — Deductions{' '}
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1065-schk-deduction-img.webp" />
                  <source srcSet="/assets/images/f1065-schk-deduction-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1065-schk-deduction-img.png"
                    loading="lazy"
                    alt="Schedule K — Deductions"
                    width={760}
                    height={99}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 17, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 12 — Section 179 Deduction
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Immediate expensing of qualifying assets.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Attach:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Form 4562 </li>
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 17, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 13 — Charitable Contributions & Investment Interest
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Includes:
                </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>Cash charitable contributions </li>
                  <li>Noncash contributions </li>
                  <li>Investment interest expense </li>
                  <li>Section 59(e) expenditures </li>
                  <li>Other deductions </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Each item may have{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    partner-level limitations
                  </span>
                  .
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 20, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Schedule K — Self-Employment, Credits & International{' '}
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 17, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 14 — Self-Employment Income
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1065-schk-self-emply-img.webp" />
                  <source srcSet="/assets/images/f1065-schk-self-emply-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1065-schk-self-emply-img.png"
                    loading="lazy"
                    alt="Line 14 — Self-Employment Income "
                    width={760}
                    height={51}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Reports amounts subject to{' '}
                  <span style={{ fontWeight: 600 }}> self-employment tax</span>{' '}
                  for partners.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 17, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 15 — Credits
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1065-schk-credits-img.webp" />
                  <source srcSet="/assets/images/f1065-schk-credits-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1065-schk-credits-img.png"
                    loading="lazy"
                    alt="Line 15 — Credits"
                    width={760}
                    height={99}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Includes:
                </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>Low-income housing credits </li>
                  <li>Rehabilitation credits </li>
                  <li>Other business credits </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Credits may be limited by{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    partner basis or at-risk rules.
                  </span>
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 17, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 16 — International Tax Items
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1065-schk-nternational-tax-img.webp" />
                  <source srcSet="/assets/images/f1065-schk-nternational-tax-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1065-schk-nternational-tax-img.png"
                    loading="lazy"
                    alt="Line 16 — International Tax Items"
                    width={760}
                    height={41}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Requires <span style={{ fontWeight: 600 }}>Schedule K-2</span>{' '}
                  and impacts partner{' '}
                  <span style={{ fontWeight: 600 }}>Schedule K-3</span>.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 17, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 17 — AMT Items
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1065-schk-amt-img.webp" />
                  <source srcSet="/assets/images/f1065-schk-amt-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1065-schk-amt-img.png"
                    loading="lazy"
                    alt="Line 17 — AMT Items"
                    width={760}
                    height={99}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Alternative Minimum Tax adjustments, such as:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Depreciation differences </li>
                  <li>Depletion </li>
                  <li>Oil and gas income </li>
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 17, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Lines 18–21 — Other Information
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1065-schk-other-infor-img.webp" />
                  <source srcSet="/assets/images/f1065-schk-other-infor-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1065-schk-other-infor-img.png"
                    loading="lazy"
                    alt="Lines 18–21 — Other Information"
                    width={760}
                    height={148}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Includes:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Tax-exempt income </li>
                  <li>Nondeductible expenses </li>
                  <li>Distributions </li>
                  <li id="schk1">Investment income/expenses </li>
                  <li>Foreign taxes paid </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 2,
                  }}
                >
                  Schedule K-1 — Partner-Level Allocation
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Each <span style={{ fontWeight: 600 }}> Schedule K-1</span>{' '}
                  shows a partner’s{' '}
                  <span style={{ fontWeight: 600 }}>share</span> of every
                  Schedule K item.{' '}
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 17, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  What Determines Allocation
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Allocations are based on:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Ownership percentage </li>
                  <li>Profit-sharing ratios </li>

                  <li>Special allocations in the partnership agreement </li>
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 17, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Example Allocation
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Partnership facts:
                </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>Ordinary income (Schedule K, Line 1): $120,000 </li>
                  <li>Two partners: </li>
                  <ul>
                    <li>Partner A: 60%</li>
                    <li>Partner B: 40% </li>
                  </ul>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  form 1065 k-1 reporting:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Partner A: $72,000 </li>
                  <li>Partner B: $48,000 </li>
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 17, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Why K-1 Accuracy Is Critical
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    1065 K 1 form errors require{' '}
                    <span style={{ fontWeight: 600 }}>
                      amended partner returns
                    </span>{' '}
                  </li>
                  <li>
                    Late K-1s trigger{' '}
                    <span id="schl" style={{ fontWeight: 600 }}>
                      per-partner penalties
                    </span>{' '}
                  </li>
                  <li>
                    IRS compares form 1065 k 1 totals directly to Schedule
                    K{' '}
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 2,
                  }}
                >
                  Schedule L — Balance Sheets per Books
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Schedule L shows the partnership’s{' '}
                  <span style={{ fontWeight: 600 }}>financial position,</span>{' '}
                  not tax values.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  It must be completed unless the partnership qualifies for the{' '}
                  <span style={{ fontWeight: 600 }}>
                    small partnership exception.
                  </span>{' '}
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 17, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Assets Section (Beginning & End of Year)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Includes:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Cash </li>
                  <li>Accounts receivable (net of bad debts) </li>
                  <li>Inventory </li>
                  <li>Investments </li>
                  <li>Loans to partners </li>
                  <li>Depreciable assets (net of depreciation)</li>
                  <li>Intangible assets </li>
                  <li>Other assets (itemized) </li>
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 17, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Liabilities & Capital
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Includes:
                </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>Accounts payable </li>
                  <li>Short-term and long-term debt </li>
                  <li>Loans from partners </li>

                  <li>Other liabilities </li>
                  <li id="schm1">Partners’ capital accounts </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}> Rule:</span> Total assets{' '}
                  <span style={{ fontWeight: 600 }}>must equal</span> total
                  liabilities plus capital.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 2,
                  }}
                >
                  Schedule M-1 — Book vs Tax Reconciliation
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1065-schm1-book-tax-img.webp" />
                  <source srcSet="/assets/images/f1065-schm1-book-tax-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1065-schm1-book-tax-img.png"
                    loading="lazy"
                    alt="Schedule M-1 — Book vs Tax Reconciliation"
                    width={760}
                    height={225}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Schedule M-1 explains why{' '}
                  <span style={{ fontWeight: 600 }}>
                    book income  ‰  taxable income.
                  </span>
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 17, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Key M-1 Differences
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Common reconciling items:
                </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>Depreciation differences </li>
                  <li>Nondeductible expenses </li>
                  <li>Tax-exempt income </li>
                  <li>Guaranteed payments </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Example
                </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>Book income: $150,000 </li>
                  <li>Tax depreciation exceeds book depreciation: $20,000 </li>
                  <li>Nondeductible expenses: $5,000 </li>
                </Box>
                <Typography
                  variant="body1"
                  id="schm2"
                  sx={{ mb: 0.5, fontWeight: 600 }}
                >
                  Taxable income:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  $150,000 – $20,000 + $5,000 ={' '}
                  <span style={{ fontWeight: 600 }}>$135,000</span>
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 2,
                  }}
                >
                  Schedule M-2 — Analysis of Partners’ Capital Accounts
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1065-schm2-analysis-img.webp" />
                  <source srcSet="/assets/images/f1065-schm2-analysis-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1065-schm2-analysis-img.png"
                    loading="lazy"
                    alt="Schedule M-2 — Analysis of Partners’ Capital Accounts"
                    width={760}
                    height={114}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Schedule M-2 tracks{' '}
                  <span style={{ fontWeight: 600 }}>
                    how partner capital changes
                  </span>{' '}
                  during the year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  This is{' '}
                  <span style={{ fontWeight: 600 }}>
                    far more important for partnerships
                  </span>{' '}
                  than AAA is for S Corps.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 17, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Capital Account Components
                </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>Beginning capital </li>
                  <li>Capital contributions </li>
                  <li>Net income or loss </li>
                  <li>Distributions </li>
                  <li>Other increases or decreases </li>
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 17, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Example (Single Partner)
                </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>Beginning capital: $80,000 </li>
                  <li>Net income: $45,000 </li>
                  <li>Distribution: $25,000 </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Ending capital:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  $80,000 + $45,000 – $25,000 ={' '}
                  <span style={{ fontWeight: 600 }}>$100,000 </span>
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 17, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Why Schedule M-2 Matters
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Determines partner{' '}
                    <span style={{ fontWeight: 600 }}>basis</span>{' '}
                  </li>
                  <li id="how">Affects deductibility of losses </li>
                  <li>Impacts taxation of distributions </li>
                  <li>Closely reviewed in IRS audits</li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 2,
                  }}
                >
                  How All Schedules Connect
                </Typography>
                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Schedule
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Purpose
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>Page 1 </TableCell>

                          <TableCell>Calculates partnership income </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Schedule K </TableCell>

                          <TableCell>Summarizes tax items </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Schedule K-1 </TableCell>

                          <TableCell>Allocates items to partners </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Schedule L </TableCell>

                          <TableCell>Shows financial position </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Schedule M-1 </TableCell>

                          <TableCell>
                            Explains book vs tax differences{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Schedule M-2 </TableCell>

                          <TableCell id="common">
                            Tracks partner capital{' '}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  A mistake in <span style={{ fontWeight: 600 }}>any one</span>{' '}
                  of these schedules often cascades into all others.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 2,
                  }}
                >
                  Common Form 1065 Filing Mistakes (and How to Avoid Them)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  These are{' '}
                  <span style={{ fontWeight: 600 }}> real-world errors</span>{' '}
                  that frequently trigger IRS notices, rejected e-files, or
                  amended Schedule K-1s.
                </Typography>

                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 17, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  1. Treating Partners Like Employees
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  <span style={{ fontWeight: 600 }}>Mistake:</span> Paying
                  partners wages and reporting them on Line 9.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Correct treatment:
                </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>
                    Partner compensation must be reported as{' '}
                    <span style={{ fontWeight: 600 }}>
                      Guaranteed Payments (Line 10)
                    </span>
                  </li>
                  <li>
                    Partners are <span style={{ fontWeight: 600 }}>not</span>{' '}
                    W-2 employees for federal tax purposes{' '}
                  </li>
                </Box>

                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Why IRS cares:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Misclassification affects self-employment tax and income
                  allocation.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 17, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  2. Incorrect Partner Ownership Percentages
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  <span style={{ fontWeight: 600 }}>Mistake:</span> Using
                  year-end ownership instead of{' '}
                  <span style={{ fontWeight: 600 }}>weighted ownership</span>{' '}
                  when partners join or leave mid-year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Correct approach:
                </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>
                    Allocate income based on the{' '}
                    <span style={{ fontWeight: 600 }}>
                      partnership agreement
                    </span>{' '}
                  </li>

                  <li>
                    Reflect partial-year ownership correctly on Schedule k-1
                    form 1065
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Impact:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Incorrect allocations lead to{' '}
                  <span style={{ fontWeight: 600 }}>amended K-1s</span> and
                  partner tax corrections.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 17, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  3. Schedule K Does Not Match Total of K-1s
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  <span style={{ fontWeight: 600 }}>Mistake:</span> Schedule K
                  totals do not reconcile with all issued K-1s.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  IRS cross-check:
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  The IRS system automatically validates:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>Schedule K totals</li>

                  <li>Aggregate K-1 totals</li>
                </Box>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Any mismatch = notice.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 17, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  4. Ignoring Schedule B Disclosures
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  <span style={{ fontWeight: 600 }}>Mistake:</span> Answering
                  “No” to foreign, ownership, or audit questions without review.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  High-risk areas:
                </Typography>

                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>Foreign bank accounts (FBAR-related) </li>

                  <li>Section 754 elections </li>
                  <li>Partnership Representative designation </li>
                  <li>Digital asset transactions </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Result:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Schedule B errors are among the{' '}
                  <span style={{ fontWeight: 600 }}>top audit triggers</span>{' '}
                  for partnerships.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 17, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  5. Capital Accounts Not Reconciled (Schedule M-2)
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  <span style={{ fontWeight: 600 }}>Mistake:</span> Ending
                  capital balances do not match:
                </Typography>

                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>Schedule L </li>
                  <li>Prior-year ending balances </li>
                  <li>Partner records </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Why this matters:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Capital account accuracy affects:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Partner basis </li>
                  <li>Loss deductibility </li>
                  <li>Distribution taxation </li>
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 17, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  6. Missing or Late Schedule K-1s
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}>Mistake:</span> Filing Form
                  1065 but delaying K-1 distribution.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Penalty exposure:
                </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>
                    Penalties may apply per partner even if the 1065 form is
                    filed on time.
                  </li>
                </Box>
                <Typography
                  variant="body1"
                  id="lesser"
                  sx={{ mb: 0, fontWeight: 600 }}
                >
                  Best practice:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Prepare K-1s <span style={{ fontWeight: 600 }}>before</span>{' '}
                  <Link
                    href="/business-tax/efile-irs-form-1065-online/"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    e-filing Form 1065
                  </Link>
                  .
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 2,
                  }}
                >
                  Lesser-Known Forms Commonly Required With Form 1065
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Many partnerships file 1065 a form correctly — but miss{' '}
                  <span style={{ fontWeight: 600 }}>required attachments,</span>{' '}
                  leading to IRS follow-ups.
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Form
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            When Required
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>
                            {' '}
                            <Link
                              href="https://www.irs.gov/forms-pubs/about-form-1125-a"
                              target="_blank"
                              sx={{ color: '#0202ff' }}
                            >
                              {' '}
                              Form 1125-A
                            </Link>{' '}
                          </TableCell>

                          <TableCell>
                            Form 1125-A If reporting Cost of Goods Sold
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            <Link
                              href="https://www.irs.gov/forms-pubs/about-form-4562"
                              target="_blank"
                              sx={{ color: '#0202ff' }}
                            >
                              Form 4562
                            </Link>{' '}
                          </TableCell>

                          <TableCell>
                            Depreciation or Section 179 deduction{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>
                            {' '}
                            <Link
                              href="https://www.irs.gov/forms-pubs/about-form-4797"
                              target="_blank"
                              sx={{ color: '#0202ff' }}
                            >
                              Form 4797
                            </Link>{' '}
                          </TableCell>

                          <TableCell>Sale of business property </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            <Link
                              href="https://www.irs.gov/forms-pubs/about-form-8825"
                              target="_blank"
                              sx={{ color: '#0202ff' }}
                            >
                              Form 8825
                            </Link>{' '}
                          </TableCell>

                          <TableCell>Rental real estate activities</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>
                            <Link
                              href="https://www.irs.gov/forms-pubs/about-form-8697"
                              target="_blank"
                              sx={{ color: '#0202ff' }}
                            >
                              Form 8697
                            </Link>{' '}
                            /{' '}
                            <Link
                              href="https://www.irs.gov/forms-pubs/about-form-8866"
                              target="_blank"
                              sx={{ color: '#0202ff' }}
                            >
                              8866
                            </Link>{' '}
                          </TableCell>

                          <TableCell>Look-back interest </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Schedule K-2 / K-3 </TableCell>

                          <TableCell>International tax items </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>
                            <Link
                              href="https://www.irs.gov/forms-pubs/about-form-8990"
                              target="_blank"
                              sx={{ color: '#0202ff' }}
                            >
                              Form 8990
                            </Link>{' '}
                          </TableCell>

                          <TableCell>Business interest limitation </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            <Link
                              href="https://www.irs.gov/forms-pubs/about-form-8996"
                              target="_blank"
                              sx={{ color: '#0202ff' }}
                            >
                              Form 8996
                            </Link>{' '}
                          </TableCell>

                          <TableCell>Qualified Opportunity Fund </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>
                            <Link
                              href="https://www.irs.gov/forms-pubs/about-form-3520"
                              target="_blank"
                              sx={{ color: '#0202ff' }}
                            >
                              Form 3520
                            </Link>{' '}
                          </TableCell>

                          <TableCell>Foreign trust transactions </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>FinCEN Form 114 </TableCell>

                          <TableCell>Foreign bank accounts (FBAR) </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Alert
                  icon={<InfoOutlinedIcon />}
                  severity="info"
                  id="checklist"
                  sx={{ mt: 2, mb: 3 }}
                >
                  <b>Instructional Tip:</b> These forms are usually triggered by{' '}
                  <span style={{ fontWeight: 600 }}>Schedule B answers</span>.
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Filing Checklist Before Submitting Form 1065
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Use this checklist to reduce errors and rejections:
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Partnership name & EIN match IRS records</li>
                  <li>Accounting method consistent with prior year</li>
                  <li>Schedule K totals match all K-1s</li>
                  <li>Capital accounts reconcile (M-2  †” L)</li>
                  <li>Schedule B answered completely and accurately</li>
                  <li>Required attachments included</li>
                  <li id="faq">K-1s prepared for all partners</li>
                  <li> Extension filed (Form 7004) if needed</li>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Frequently Asked Questions (Instruction-Specific,
                  <br /> Non-Overlapping)
                </Typography>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  1. Does a partnership with no income still need to file Form
                  1065?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Yes. Many partnerships are required to file{' '}
                  <span style={{ fontWeight: 600 }}>
                    even with zero income,
                  </span>{' '}
                  especially if they have expenses, partners, or ongoing legal
                  existence.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  2. Can a partnership report a loss on Form 1065?
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Yes. Losses pass through to partners and may be deductible,
                  subject to:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Basis rules </li>
                  <li>At-risk rules </li>
                  <li>Passive activity limitations </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  3. What is the difference between Guaranteed Payments and
                  Profit Allocation?
                </Typography>
                <Typography
                  component="ul"
                  variant="body1"
                  sx={{ mb: 0, '& li': { mb: 1.2 } }}
                >
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Guaranteed Payments:
                    </span>{' '}
                    Fixed, paid regardless of profits; subject to
                    self-employment tax
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Profit Allocation:</span>{' '}
                    Based on ownership or agreement; depends on partnership
                    results
                  </li>
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  They are reported{' '}
                  <span style={{ fontWeight: 600 }}>separately</span> and taxed
                  differently.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  4. Is Schedule M-2 required every year?
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Not always. It is generally required when:
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>Assets or receipts exceed IRS thresholds, or </li>
                  <li>The partnership has significant capital activity </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  However, maintaining capital accuracy is always recommended.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  5. Who should be the Partnership Representative?
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  The PR should be someone who:
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>Understands partnership tax rules </li>
                  <li>Can respond to IRS inquiries </li>
                  <li>Has authority to bind the partnership </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The PR does <span style={{ fontWeight: 600 }}>not</span> have
                  to be a partner.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  6. Can partners file their tax returns before receiving
                  Schedule K-1?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  No. Partners must wait for Schedule K1 form 1065, as it
                  contains essential income and deduction details.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  7. What happens if a Schedule K-1 is wrong?
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  The partnership must issue:
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>Corrected K-1s,</span>{' '}
                    and{' '}
                  </li>
                  <li>
                    Potentially file an{' '}
                    <span style={{ fontWeight: 600 }}>
                      amended Form 1065
                    </span>{' '}
                  </li>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              A smarter way to file your partnership tax return
            </Typography>
            <Typography
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              From income reporting to partner allocations, TaxZerone supports
              every step of Form 1065 filing.
            </Typography>
            <Box sx={{ maxWidth: 480, textAlign: 'left', m: 'auto' }}>
              <Box
                component="ul"
                sx={{
                  mb: 2,
                  '& li': { mb: 1.2, listStyleType: 'none', display: 'block' },
                }}
              >
                <li>
                  <CheckCircleIcon sx={{ mb: -0.7, color: '#1da94a' }} />{' '}
                  IRS-authorized partnership e-filing
                </li>
                <li>
                  <CheckCircleIcon sx={{ mb: -0.7, color: '#1da94a' }} />{' '}
                  Real-time error checks to avoid rejections
                </li>
                <li>
                  <CheckCircleIcon sx={{ mb: -0.7, color: '#1da94a' }} />{' '}
                  Automatic Schedule K-1 generation for all partners
                </li>
                <li>
                  <CheckCircleIcon sx={{ mb: -0.7, color: '#1da94a' }} /> Secure
                  cloud storage (
                  <span style={{ fontWeight: 600 }}>ZeroneVault</span>)
                </li>
                <li>
                  <CheckCircleIcon sx={{ mb: -0.7, color: '#1da94a' }} />{' '}
                  Affordable pricing for partnerships of all sizes
                </li>
              </Box>
            </Box>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1065instructions`}
              >
                Start Your Form 1065 Filing Today
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form1065Instructions;
